import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import { getFieldErrorMessages } from 'global/helpers/form'
import { type ReactElement } from 'react'
import { Controller, type FieldValues } from 'react-hook-form'
import type { KNFormRadioGroupProps } from './types'

const KNCheckboxGroup = styled(Stack)(({ theme }) => ({
  variants: [
    {
      props: { variant: 'outlined' },
      style: {
        border: `1px solid ${theme.palette.primary.light}`,
        borderRadius: theme.borders.borderRadius['md'],
        '& .MuiFormControlLabel-root': {
          margin: 0,
          '&:hover:not(.Mui-disabled)': {
            backgroundColor: theme.alpha(theme.palette.secondary.main, theme.palette.action.hoverOpacity),
          },
          '&:has(.Mui-checked)': {
            backgroundColor: theme.alpha(theme.palette.secondary.main, theme.palette.action.selectedOpacity),
            '&:hover:not(.Mui-disabled)': {
              backgroundColor: theme.alpha(theme.palette.secondary.main, theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity),
            },
          },
          '&.Mui-error .MuiCheckbox-root': {
            color: theme.palette.error.main,
          },
        },
        '& .MuiFormControlLabel-root + .MuiFormControlLabel-root': {
          borderTop: `1px solid ${theme.palette.primary.light}`,
        },
      },
    },
  ],
}))

const toggleValue = (value: string, selectedValues: string[]) =>
  selectedValues.includes(value)
    ? selectedValues.filter((v) => v !== value)
    : [...selectedValues, value]

const KNFormCheckboxGroup = <T extends FieldValues>({
  name,
  control,
  rules,
  label,
  options,
  sx,
  disabled,
  variant = 'standard',
}: KNFormRadioGroupProps<T>): ReactElement => (
  <Controller
    name={name}
    control={control}
    rules={rules}
    render={({ field: { onChange, value }, fieldState: { invalid, error }, formState }): ReactElement => (
      <FormControl fullWidth sx={sx} error={invalid} disabled={formState.isSubmitting}>
        {label && (
          <FormLabel sx={{ marginBottom: 0.5, color: disabled ? 'text.disabled' : 'primary.main' }}>{label}</FormLabel>
        )}
        <KNCheckboxGroup variant={variant}>
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              disabled={option.disabled}
              control={
                <Checkbox
                  checked={value.includes(option.value) ?? false}
                  onChange={() => onChange(toggleValue(option.value, value))}
                />
              }
              label={option.label}
            />
          ))}
        </KNCheckboxGroup>
        {error?.message && <FormHelperText>{getFieldErrorMessages(error)}</FormHelperText>}
      </FormControl>
    )}
  />
)

export default KNFormCheckboxGroup
